// Lightweight, framework-agnostic theme manager.
// - Preference is "light" or "dark" and is stored in localStorage, mirroring
//   the existing columnWidth.ts pattern. Following the OS was removed: the
//   choice is now always explicit.
// - The resolved theme ("light" | "dark") is applied as a `data-theme`
//   attribute on <html>; SCSS keys its dark overrides off `[data-theme="dark"]`.
// - `prefersDark()` survives only to give a sensible first value to anyone who
//   still has the retired "system" preference stored.

export type ThemePreference = "light" | "dark";
export type ResolvedTheme = "light" | "dark";

/**
 * Colour variants of the dark theme. They share one structure - same roles,
 * same relative brightness, same accent - and differ only in the colour cast of
 * the greys, so backgrounds, lines and headings keep their relationships. The
 * palettes themselves live in _dark.scss as CSS custom properties.
 */
// Mirrors darkVariantNames in encharge-domain/helpers/constants, which cannot
// carry the literal type (it has to stay plain JS - see the note there).
// variants.consistency.spec.ts fails if the two drift apart.
export type DarkVariant =
  | "charcoal"
  | "midnight"
  | "carbon"
  | "espresso"
  | "forest"
  | "slate"
  | "dusk"
  | "ash"
  | "latte"
  | "sage";

export const DARK_VARIANTS: Array<{
  value: DarkVariant;
  label: string;
  /** the canvas colour - the pre-paint background, and the swatch's darkest stop */
  swatch: string;
  /** the lifted panel and raised-surface tones, so a picker swatch can show the
   * variant's whole range instead of one near-black square (five near-black
   * squares are indistinguishable, which defeats the point of a picker) */
  panel: string;
  raised: string;
}> = [
  {
    value: "charcoal",
    label: "Charcoal",
    swatch: "#0b0e15",
    panel: "#13161f",
    raised: "#2e3442",
  },
  {
    value: "midnight",
    label: "Midnight",
    swatch: "#0a0f1e",
    panel: "#111828",
    raised: "#2b3a56",
  },
  {
    value: "carbon",
    label: "Carbon",
    swatch: "#0d0d0d",
    panel: "#161616",
    raised: "#343434",
  },
  {
    value: "espresso",
    label: "Espresso",
    swatch: "#14100c",
    panel: "#1d1813",
    raised: "#40362c",
  },
  {
    value: "forest",
    label: "Forest",
    swatch: "#0a1210",
    panel: "#111b18",
    raised: "#2b3f38",
  },
  // The softer family. Each takes its LIGHTNESS from slate and its hue from the
  // dark variant above it, so these five differ from one another in exactly the
  // way the dark five do: same relative brightness, only the cast changes.
  {
    value: "slate",
    label: "Slate",
    swatch: "#222835",
    panel: "#2a3140",
    raised: "#4a5568",
  },
  {
    value: "dusk",
    label: "Dusk",
    swatch: "#162141",
    panel: "#202d4a",
    raised: "#3b5077",
  },
  {
    value: "ash",
    label: "Ash",
    swatch: "#2c2c2c",
    panel: "#353535",
    raised: "#595959",
  },
  {
    value: "latte",
    label: "Latte",
    swatch: "#362b21",
    panel: "#40352a",
    raised: "#695949",
  },
  {
    value: "sage",
    label: "Sage",
    swatch: "#1f3832",
    panel: "#29413a",
    raised: "#486a5e",
  },
];

const isDarkVariant = (value: any): value is DarkVariant =>
  DARK_VARIANTS.some((variant) => variant.value === value);

export const THEME_STORAGE_KEY = "encharge-theme";
export const DARK_VARIANT_STORAGE_KEY = "encharge-dark-variant";
/**
 * The last appearance we know the server holds. It is what lets us tell a
 * genuine remote change (someone switched theme on another machine) apart from
 * a stale read (our own save has not landed yet). Without it, a failed save is
 * silently undone by the next account load - the user picks a colour, it
 * sticks, and then quietly reverts.
 */
export const SYNCED_STORAGE_KEY = "encharge-appearance-synced";

const listeners = new Set<() => void>();

/**
 * How a change reaches the person's record. Injected by the app at startup
 * rather than imported, so this module keeps no network dependency and stays
 * straightforward to test.
 */
export interface Appearance {
  themePreference?: ThemePreference;
  darkVariant?: DarkVariant;
}

/** Resolves true when the server accepted the change. */
type AppearancePersister = (appearance: Appearance) => Promise<boolean> | void;

let persist: AppearancePersister = () => undefined;

export const setAppearancePersister = (
  persister: AppearancePersister
): void => {
  persist = persister;
};

const prefersDark = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

// Public, recipient-facing routes (unsubscribe / communication preferences,
// hosted native forms, ...) must always render light: they represent the
// customer's own branded pages and should not inherit the app's dark theme
// based on the *recipient's* OS. Mirrored in the anti-flash script in index.html.
const isPublicRoute = (): boolean =>
  typeof window !== "undefined" &&
  window.location.pathname.indexOf("/public/") === 0;

export const getPreference = (): ThemePreference => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      return stored;
    }
    // "system" is no longer offered; anyone still holding that preference is
    // migrated to an explicit choice rather than silently following the OS.
    if (stored === "system") {
      return prefersDark() ? "dark" : "light";
    }
  } catch (e) {
    // localStorage may be unavailable (private mode) — fall through
  }
  return "light";
};

export const getDarkVariant = (): DarkVariant => {
  try {
    const stored = localStorage.getItem(DARK_VARIANT_STORAGE_KEY);
    if (isDarkVariant(stored)) {
      return stored;
    }
  } catch (e) {
    // localStorage may be unavailable (private mode) - fall through
  }
  return "charcoal";
};

export const getResolvedTheme = (
  preference: ThemePreference = getPreference()
): ResolvedTheme => preference;

const LIGHT_BACKGROUND = "#ffffff";

/** The canvas colour for a resolved theme, mirroring _dark.scss. */
const rootBackground = (resolved: ResolvedTheme): string => {
  if (resolved === "light") {
    return LIGHT_BACKGROUND;
  }
  const variant = DARK_VARIANTS.find((v) => v.value === getDarkVariant());
  return variant ? variant.swatch : DARK_VARIANTS[0].swatch;
};

export const applyTheme = (): void => {
  if (typeof document === "undefined") return;
  const resolved = isPublicRoute() ? "light" : getResolvedTheme();
  const root = document.documentElement;
  root.setAttribute("data-theme", resolved);
  // The variant only means anything in dark, but it is left on the element in
  // both so switching back and forth does not lose the choice.
  root.setAttribute("data-dark-variant", getDarkVariant());
  // index.html paints this inline before first paint to avoid a white flash.
  // An inline style beats the stylesheet, so it has to be updated here too -
  // otherwise switching to light would leave the dark background stuck on.
  root.style.backgroundColor = rootBackground(resolved);
  root.style.colorScheme = resolved;
};

/** Has anyone actually chosen on this device, as opposed to been defaulted? */
const hasStoredChoice = (): boolean => {
  try {
    return Boolean(
      localStorage.getItem(THEME_STORAGE_KEY) ||
        localStorage.getItem(DARK_VARIANT_STORAGE_KEY)
    );
  } catch (e) {
    return false;
  }
};

const readSynced = (): Appearance | undefined => {
  try {
    const raw = localStorage.getItem(SYNCED_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Appearance) : undefined;
  } catch (e) {
    return undefined;
  }
};

const writeSynced = (appearance: Appearance): void => {
  try {
    localStorage.setItem(SYNCED_STORAGE_KEY, JSON.stringify(appearance));
  } catch (e) {
    // storage unavailable - we simply lose the ability to detect a stale read
  }
};

/** Fire the save, and remember what the server has once it accepts. */
const save = (appearance: Appearance): void => {
  const result = persist(appearance);
  if (result && typeof (result as Promise<boolean>).then === "function") {
    (result as Promise<boolean>).then((ok) => {
      if (ok) {
        writeSynced({
          themePreference: getPreference(),
          darkVariant: getDarkVariant(),
        });
      }
    });
  }
};

/**
 * Adopt the appearance saved on the person's own record.
 *
 * The server is what makes a choice follow someone to another browser, but it
 * must not be allowed to undo a choice made here that has not been saved yet.
 * So it wins only when it has actually CHANGED since we last synced - which
 * means somebody set it on another machine. If it matches what we last synced,
 * nothing happened remotely, and a local value that differs is our own unsaved
 * change: keep it, and try the save again.
 *
 * Values are written straight to storage rather than through the setters, so
 * adopting the server's state does not echo back to the server as a change.
 */
export const adoptSavedAppearance = (saved: {
  themePreference?: string | null;
  darkVariant?: string | null;
}): void => {
  const server: Appearance = {
    themePreference:
      saved.themePreference === "light" || saved.themePreference === "dark"
        ? saved.themePreference
        : undefined,
    darkVariant: isDarkVariant(saved.darkVariant)
      ? saved.darkVariant
      : undefined,
  };
  const synced = readSynced();
  const serverChanged =
    !synced ||
    synced.themePreference !== server.themePreference ||
    synced.darkVariant !== server.darkVariant;

  if (!serverChanged) {
    // Nothing changed remotely. If this browser holds something different, our
    // own save never landed - retry it rather than throwing the choice away.
    //
    // Only when the browser actually holds a choice, though: the getters below
    // coerce an empty store to light/charcoal, so reading them unconditionally
    // made this fire for someone who had never picked anything, writing a
    // default onto their record for no reason.
    if (!hasStoredChoice()) {
      return;
    }
    const local: Appearance = {
      themePreference: getPreference(),
      darkVariant: getDarkVariant(),
    };
    if (
      local.themePreference !== server.themePreference ||
      local.darkVariant !== server.darkVariant
    ) {
      save(local);
    }
    return;
  }

  let changed = false;
  try {
    if (
      server.themePreference &&
      localStorage.getItem(THEME_STORAGE_KEY) !== server.themePreference
    ) {
      localStorage.setItem(THEME_STORAGE_KEY, server.themePreference);
      changed = true;
    }
    if (
      server.darkVariant &&
      localStorage.getItem(DARK_VARIANT_STORAGE_KEY) !== server.darkVariant
    ) {
      localStorage.setItem(DARK_VARIANT_STORAGE_KEY, server.darkVariant);
      changed = true;
    }
  } catch (e) {
    // storage unavailable - the theme still applies for this session
  }
  writeSynced(server);
  if (changed) {
    applyTheme();
    notify();
  }
};

export const setDarkVariant = (variant: DarkVariant): void => {
  try {
    localStorage.setItem(DARK_VARIANT_STORAGE_KEY, variant);
  } catch (e) {
    // ignore storage errors (private mode, quota, etc.)
  }
  applyTheme();
  notify();
  save({ themePreference: getPreference(), darkVariant: variant });
};

const notify = () => listeners.forEach((listener) => listener());

export const setPreference = (preference: ThemePreference): void => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch (e) {
    // ignore storage errors (private mode, quota, etc.)
  }
  applyTheme();
  notify();
  save({ themePreference: preference, darkVariant: getDarkVariant() });
};

// Subscribe to preference/resolved-theme changes. Returns an unsubscribe fn.
export const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

let initialised = false;
export const initTheme = (): void => {
  applyTheme();
  if (initialised) {
    return;
  }
  initialised = true;
  // Nothing to subscribe to any more: the theme no longer follows the OS, so a
  // prefers-color-scheme change must not move a user off their explicit choice.
};
